import { useEffect, useMemo, useRef, useState } from 'react';
import { useMotionReady } from './useMotionReady';

let webglSupport: boolean | null = null;

/**
 * One-off probe. Devices and browsers that refuse a WebGL context otherwise
 * let three.js throw and flood the console on every canvas mount. Creating a
 * context is a 200ms main-thread task on a mid-range phone, so it runs only
 * once a canvas is actually wanted, and the probe context is released at once
 * rather than held until garbage collection.
 */
function detectWebgl(): boolean {
  if (webglSupport !== null) {
    return webglSupport;
  }

  try {
    const canvas = document.createElement('canvas');
    const context =
      canvas.getContext('webgl2') ?? canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl');
    webglSupport = Boolean(context);
    (context as WebGLRenderingContext | null)?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch {
    webglSupport = false;
  }

  return webglSupport;
}

/**
 * Phones pay for multisampling and high pixel ratios far more than they gain
 * from them, and three canvases share the same GPU here.
 */
function isHandheld(): boolean {
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

/**
 * Reports whether a canvas should render at all, whether it is on screen, and
 * how hard it may push the GPU. Off-screen canvases stop their render loop
 * instead of burning frames behind the fold.
 */
export function useCanvasActivity<TElement extends HTMLElement>() {
  const containerRef = useRef<TElement>(null);
  const [onScreen, setOnScreen] = useState(false);
  const [reached, setReached] = useState(false);
  const motionReady = useMotionReady();
  const handheld = useMemo(() => (typeof window === 'undefined' ? false : isHandheld()), []);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        setOnScreen(visible);

        if (visible) {
          setReached(true);
        }
      },
      { rootMargin: '15% 0px' },
    );

    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  return {
    containerRef,
    /**
     * Sticky once the section has been approached. Creating a WebGL context and
     * decoding its texture costs hundreds of milliseconds of main thread, so
     * canvases below the fold must not do it during page load.
     */
    active: reached && motionReady && detectWebgl(),
    onScreen,
    dpr: (handheld ? [1, 1.25] : [1, 1.6]) as [number, number],
    antialias: !handheld,
  };
}
